import { ThemeEvents } from '@theme/events';
import { ProductCompare } from '@theme/product-compare';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Renders the compare products grid client-side, since the compared products
 * only exist in localStorage and aren't known at the time the page is first
 * requested from the server.
 *
 * Reuses the same "fetch arbitrary products by id" trick as recently-viewed
 * products in predictive search: request this section rendered against the
 * `/search` route with an `id:x OR id:y` query, which gives the section
 * access to the real `search.results` product objects (see
 * assets/predictive-search.js).
 */
class ComparePageComponent extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.compareUpdate, this.#refresh);

    // Whether there's actually a saved list isn't known until the fetch below resolves — the
    // compared products only exist in localStorage, invisible to the server render this
    // element started with. Show the loading state instead of whatever that render guessed
    // (usually "nothing saved", since a plain page load has no query params to go on), so a
    // shopper who does have a saved list doesn't see a misleading empty-state flash.
    this.#showLoading();
    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.compareUpdate, this.#refresh);
  }

  #showLoading() {
    const emptyState = this.querySelector('.compare-page__empty');
    const spinnerWrap = this.querySelector('.compare-page__spinner-wrap');
    const emptyText = this.querySelector('.compare-page__empty p');
    const actions = this.querySelector('.compare-placeholder__actions');
    const stickyBand = this.querySelector('compare-sticky-band-component');

    if (emptyState) emptyState.hidden = false;
    if (spinnerWrap) spinnerWrap.hidden = false;
    if (emptyText) emptyText.hidden = true;
    if (actions) actions.hidden = true;
    if (stickyBand) stickyBand.hidden = true;
  }

  #refresh = async () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const ids = ProductCompare.getProducts().map((product) => product.id);
    if (ids.length === 0) {
      this.#renderEmpty();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
    const newContent = new DOMParser().parseFromString(html, 'text/html').querySelector('compare-page-component');

    // Replaces .compare-page__empty (spinner included) along with everything else, with
    // markup rendered from the fetch above — which does know the real state, so it's already
    // showing the correct grid or empty state without needing any further JS toggling here.
    if (newContent) this.innerHTML = newContent.innerHTML;
  };

  #renderEmpty() {
    const emptyState = this.querySelector('.compare-page__empty');
    const spinnerWrap = this.querySelector('.compare-page__spinner-wrap');
    const emptyText = this.querySelector('.compare-page__empty p');
    const actions = this.querySelector('.compare-placeholder__actions');
    const stickyBand = this.querySelector('compare-sticky-band-component');

    if (stickyBand) stickyBand.hidden = true;
    if (spinnerWrap) spinnerWrap.hidden = true;
    if (emptyText) emptyText.hidden = false;
    if (actions) actions.hidden = false;
    if (emptyState) emptyState.hidden = false;
  }
}

if (!customElements.get('compare-page-component')) {
  customElements.define('compare-page-component', ComparePageComponent);
}
